import { ItemReference, GameState, DefaultGameState } from "@shared/types";
import styles from "../../styles";
import { GameStateService } from "../../services/GameStateService";
import { GameRouteService } from "../../services/GameRouteService";

/**
 * Risk configuration
 */
interface RiskConfig {
    /** Default duration in seconds before game over (when risk = 0) */
    defaultDuration: number;
    /** Multiplier for risk calculation (optional) */
    riskMultiplier?: number;
    /** Minimum time between risk persistence calls (in milliseconds) */
    persistInterval?: number;
}

/**
 * Component for rendering a risk bar that depletes over time
 * When it reaches zero, the game is over
 */
export class RiskBarComponent extends HTMLElement {
    /** Default risk configuration */
    private static readonly DEFAULT_CONFIG: RiskConfig = {
        defaultDuration: 300, // 5 minutes in seconds
        riskMultiplier: 1,
        persistInterval: 5000, // Save risk level every 5 seconds by default
    };

    /** Current risk level (0-100) */
    private _riskLevel: number = 100;

    /** Total risk from inventory items */
    private _totalRisk: number = 0;

    /** Animation frame ID for the timer */
    private _animationFrameId: number | null = null;

    /** Last timestamp for animation frame */
    private _lastTimestamp: number = 0;

    /** Risk decrease rate per second */
    private _decreaseRate: number = 1;

    /** Risk configuration */
    private _config: RiskConfig = { ...RiskBarComponent.DEFAULT_CONFIG };

    /** Last time risk was persisted */
    private _lastPersistTime: number = 0;

    /** Flag to track if a save is pending */
    private _persistPending: boolean = false;

    // DOM element references for efficient updates
    private _timerElement: HTMLElement | null = null;
    private _riskBarElement: HTMLElement | null = null;
    private _riskLevelElement: HTMLElement | null = null;
    private _timeScaleElement: HTMLElement | null = null;

    /** Game state service instance */
    private _gameStateService: GameStateService = GameStateService.getInstance();

    /** Game route service instance */
    private _gameRouteService: GameRouteService = new GameRouteService();

    /** Unsubscribe function for game state changes */
    private _unsubscribe: (() => void) | null = null;

    /**
     * Load persisted risk level from game state
     */
    private async loadPersistedRisk(): Promise<void> {
        try {
            const gameState: GameState = await this._gameRouteService.getGameState();
            if (gameState.type === "default") {
                const defaultState: DefaultGameState = gameState;
                this._riskLevel = (defaultState as { riskLevel?: number }).riskLevel ?? 100;
            }
        }
        catch (error) {
            console.error("Failed to load risk level:", error);
        }
    }

    /**
     * Save current risk level to game state
     * Uses a debounce mechanism to limit backend requests
     */
    private async persistRisk(): Promise<void> {
        // Mark that we have a pending update
        this._persistPending = true;
        const now: number = Date.now();
        const timeSinceLastPersist: number = now - this._lastPersistTime;
        // Check if enough time has passed since the last persist
        if (timeSinceLastPersist >= (this._config.persistInterval || 5000)) {
            try {
                await this._gameRouteService.updateRisk(this._riskLevel);
                this._lastPersistTime = now;
                this._persistPending = false;
            }
            catch (error) {
                console.error("Failed to save risk level:", error);
            }
        }
    }

    /**
     * Set the inventory items to calculate total risk
     */
    public set items(items: ItemReference[]) {
        const previousRisk: number = this._totalRisk;
        this._totalRisk = this.calculateTotalRisk(items);

        this.updateDecreaseRate();

        // Only update necessary elements when risk changes
        if (previousRisk !== this._totalRisk) {
            this.updateInfoElements();
        }
    }

    /**
     * Set custom configuration for the risk bar
     */
    public set config(config: Partial<RiskConfig>) {
        this._config = {
            ...this._config,
            ...config,
        };
        this.updateDecreaseRate();
        this.updateInfoElements();
    }

    /**
     * Called when the element is connected to the DOM
     */
    public async connectedCallback(): Promise<void> {
        this.setupInitialDom();
        await this.loadPersistedRisk();
        // Check game state when component is connected
        await this._gameStateService.checkGameState();
        this._unsubscribe = this._gameStateService.subscribe(this.handleGameStateChange.bind(this));
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.stopAnimation();
        if (this._unsubscribe) {
            this._unsubscribe();
            this._unsubscribe = null;
        }
    }

    /**
     * Handle game state changes
     */
    private async handleGameStateChange(started: boolean): Promise<void> {
        // Ensure started is a boolean
        const isStarted: boolean = Boolean(started);
        this.setAttribute("data-started", isStarted.toString());
        if (isStarted) {
            this.startAnimation();
        }
        else {
            this.stopAnimation();
            // Reset risk level when game is not started
            this._riskLevel = 100;
            void this.persistRisk();
            this.updateBarElements();
        }
        // Check game state after handling the change
        await this._gameStateService.checkGameState();
    }

    /**
     * Setup the initial DOM structure with optimized element creation
     */
    private setupInitialDom(): void {
        // Add the styles once
        const styleElement: HTMLStyleElement = document.createElement("style");
        styleElement.textContent = styles.risk || "";

        // Create components with templates for better performance
        const template: HTMLTemplateElement = document.createElement("template");
        template.innerHTML = `
            <div class="risk-timer">
                <span class="risk-timer-label">SECURITY RISK</span>
                <span class="risk-timer-time"></span>
            </div>
            <div class="risk-bar-container">
                <div class="risk-bar risk-level-100"></div>
            </div>
            <div class="risk-info">
                <div class="risk-info-stat">
                    <span>Risk Level:</span>
                    <span class="risk-info-value"></span>
                </div>
                <div class="risk-info-stat">
                    <span>Time Scale:</span>
                    <span class="risk-info-value"></span>
                </div>
            </div>
        `;

        // Clone and append the template content
        const content: DocumentFragment = template.content.cloneNode(true) as DocumentFragment;
        this.appendChild(styleElement);
        this.appendChild(content);

        // Cache DOM references for efficient updates
        this._timerElement = this.querySelector(".risk-timer-time");
        this._riskBarElement = this.querySelector(".risk-bar");
        this._riskLevelElement = this.querySelector(".risk-info-stat:first-child .risk-info-value");
        this._timeScaleElement = this.querySelector(".risk-info-stat:last-child .risk-info-value");

        // Initial update of all elements
        this.updateBarElements();
        this.updateInfoElements();
    }

    /**
     * Get time scale based on total risk
     */
    private getTimeScale(): number {
        return this._totalRisk <= 0 ? 1 : 1 + (this._totalRisk / 10);
    }

    /**
     * Start smooth animation using requestAnimationFrame
     */
    private startAnimation(): void {
        if (this._animationFrameId !== null) {
            return;
        }

        this._lastTimestamp = performance.now();
        this._animationFrameId = requestAnimationFrame(this.animationLoop.bind(this));
    }

    /**
     * Animation loop for smooth risk decrease
     */
    private animationLoop(timestamp: number): void {
        // Calculate elapsed time in seconds since last frame
        const deltaTime: number = (timestamp - this._lastTimestamp) / 1000;
        this._lastTimestamp = timestamp;

        // Decrease risk level based on time elapsed and decrease rate
        this._riskLevel = Math.max(0, this._riskLevel - (this._decreaseRate * deltaTime));

        // Check if we need to persist the risk level
        const now: number = Date.now();
        const timeSinceLastPersist: number = now - this._lastPersistTime;
        // Persist the current risk level when enough time has passed or if we have a pending update
        if (this._persistPending && timeSinceLastPersist >= (this._config.persistInterval || 5000)) {
            void this.persistRisk();
        }
        else if (timeSinceLastPersist >= (this._config.persistInterval || 5000)) {
            // If enough time has passed, we should persist
            void this.persistRisk();
        }

        // Update DOM with smooth animation
        this.updateBarElements();

        // Game over when risk reaches 0
        if (this._riskLevel <= 0) {
            this.triggerGameOver();
            return;
        }

        // Continue animation loop
        this._animationFrameId = requestAnimationFrame(this.animationLoop.bind(this));
    }

    /**
     * Stop the animation
     */
    private stopAnimation(): void {
        if (this._animationFrameId !== null) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }
    }

    /**
     * Update the decrease rate based on total risk
     */
    private updateDecreaseRate(): void {
        const baseSpeed: number = 100 / this._config.defaultDuration;
        const timeScale: number = this.getTimeScale();
        this._decreaseRate = baseSpeed * timeScale;
    }

    /**
     * Calculate the total risk from inventory items
     */
    private calculateTotalRisk(items: ItemReference[]): number {
        const baseRisk: number = items.reduce((sum, item) => sum + (item.riskFactor || 0), 0);
        const multiplier: number = this._config.riskMultiplier || 1;
        return baseRisk * multiplier;
    }

    /**
     * Trigger game over when risk level reaches zero
     */
    private triggerGameOver(): void {
        this.stopAnimation();
        // Reset risk level on game over
        this._riskLevel = 100;
        // Force an immediate save of the risk level on game over
        this._lastPersistTime = 0;
        void this.persistRisk();

        // Dispatch a custom event that can be caught by parent components
        this.dispatchEvent(new CustomEvent("gameOver", {
            bubbles: true,
            detail: { riskLevel: 0 },
        }));
    }

    /**
     * Update the risk bar elements with smooth transition
     */
    private updateBarElements(): void {
        if (!this._riskBarElement || !this._timerElement) return;

        // Add CSS transition for smooth bar movement if not already present
        if (!this._riskBarElement.style.transition) {
            this._riskBarElement.style.transition = "width 0.1s linear, background-color 0.5s ease";
        }

        // Update risk bar width with smooth transition
        this._riskBarElement.style.width = `${this._riskLevel}%`;

        // Update the risk level class for color changes
        // Remove any existing risk-level classes
        const classList: string[] = Array.from(this._riskBarElement.classList)
            .filter(className => !className.startsWith("risk-level-"));

        // Round to nearest 10 for class names (risk-level-100, risk-level-90, etc.)
        const roundedLevel: number = Math.round(this._riskLevel / 10) * 10;
        classList.push(`risk-level-${roundedLevel}`);

        // Add warning class for low risk
        const isLowRisk: boolean = this._riskLevel <= 20;
        if (isLowRisk) {
            classList.push("risk-flashing");
        }

        // Apply all classes
        this._riskBarElement.className = classList.join(" ");

        // Update timer display
        const totalSecondsLeft: number = (this._riskLevel / 100) * this._config.defaultDuration;
        const minutes: number = Math.floor(totalSecondsLeft / 60);
        const seconds: number = Math.floor(totalSecondsLeft % 60);
        this._timerElement.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }

    /**
     * Update the info elements
     */
    private updateInfoElements(): void {
        if (!this._riskLevelElement || !this._timeScaleElement) return;

        const timeScale: number = this.getTimeScale();
        this._riskLevelElement.textContent = this._totalRisk.toFixed(1);
        this._timeScaleElement.textContent = `${timeScale.toFixed(1)}x`;
    }
}

// Register the component
customElements.define("game-risk-bar", RiskBarComponent);
